import { registerPlugin } from '@capacitor/core';
import { isAndroid, isNative } from '../utils/platformUtils';
import { INVITE_REFERRER_KEY } from '../config/appLinks';

// Referrer d'installation Google Play : le dernier maillon du parcours
// d'invitation sur Android.
//
// La page d'invitation met le jeton de passage du compte CHOISI dans le lien
// Play (`&referrer=upscale_invite%3D<jeton>`, cf. buildPlayStoreUrl). Play
// garde cette chaine et la rend a l'app installee, une fois pour toutes. Le
// premier lancement la lit et ouvre la session : plus rien a taper, plus de
// retour dans l'email, plus de bouton "Open the app" a trouver.
//
// Le compte choisi, pas celui de l'adresse du mail (S5) : la cle du referrer
// n'a pas change, ce qu'elle transporte si.
//
// Ce que ce chemin ne fait PAS : debloquer quoi que ce soit. Le claim serveur a
// deja pose l'acces sur le compte pendant le parcours web ; ici on se connecte,
// et on constate (App Store 3.1.1, meme regle que le deep link).
//
// Android uniquement. iOS n'a pas d'equivalent, et le web n'a rien a installer.

const InstallReferrer = registerPlugin('InstallReferrer');

// Une SEULE lecture dans la vie de l'installation. L'API rend la meme chaine a
// chaque appel, indefiniment : sans garde persistante, chaque lancement
// rejouerait l'echange et brulerait un usage du lien. Miroir du LAUNCH_READ_KEY
// de useInviteLink, en localStorage parce que la garde doit traverser les
// rechargements ET les relances, pas seulement l'onglet.
//
// Une reinstallation vide le localStorage : la garde repart a zero et le meme
// referrer peut etre relu. C'est assume, le vrai plafond est cote serveur (le
// jeton de passage ne s'echange qu'une fois et meurt en 30 minutes). Un
// referrer rejoue se fait donc refuser, et ce chemin est silencieux : personne
// ne voit rien.
const READ_KEY = 'upscale_install_referrer_read';

function wasRead() {
  try {
    return localStorage.getItem(READ_KEY) === '1';
  } catch {
    // Stockage indisponible : on prefere ne rien lire plutot que risquer un
    // rejeu a chaque lancement.
    return true;
  }
}

export function markInstallReferrerRead() {
  try {
    localStorage.setItem(READ_KEY, '1');
  } catch {
    // Rien a marquer : wasRead() rend true dans ce cas de toute facon.
  }
}

// La chaine brute telle que Play la rend. Deux formes vues selon les versions du
// Play Store : deja decodee (`upscale_invite=abc`) ou encore encodee
// (`upscale_invite%3Dabc`), d'ou la seconde passe.
//
// Tout ce qui n'est pas un de NOS jetons rend une chaine vide, sans un mot : la
// grande majorite des installations arrivent avec un referrer organique
// (`utm_source=google-play&utm_medium=organic`), et il n'y a rien a en dire.
export function parseReferrerToken(raw) {
  const text = (raw || '').trim();
  if (!text) return '';
  const pick = (value) => {
    try {
      return (new URLSearchParams(value).get(INVITE_REFERRER_KEY) || '').trim();
    } catch {
      return '';
    }
  };
  let token = pick(text);
  if (!token) {
    try {
      token = pick(decodeURIComponent(text));
    } catch {
      // Chaine mal encodee : elle ne vient pas de nous.
    }
  }
  // Les jetons du serveur sont des base64url de 32 octets (43 caracteres). Une
  // valeur d'une autre forme ne sera pas echangee : autant ne pas appeler le
  // serveur pour le decouvrir.
  return /^[A-Za-z0-9_-]{20,128}$/.test(token) ? token : '';
}

// Rend le jeton une seule fois, ou une chaine vide dans tous les autres cas.
//
// La garde n'est posee qu'APRES une reponse du natif : un echec de connexion au
// Play Store (service pas encore lie au premier lancement) doit pouvoir
// retenter au lancement suivant, alors qu'une reponse, meme vide, est
// definitive.
export async function takeInstallReferrerToken() {
  if (!isNative() || !isAndroid()) return '';
  if (wasRead()) return '';
  let referrer = '';
  try {
    const res = await InstallReferrer.getInstallReferrer();
    referrer = res?.referrer || '';
  } catch (err) {
    // Play indisponible : rien n'est marque, le lancement suivant reessaiera.
    console.warn('[referrer] lecture impossible:', err?.message || err);
    return '';
  }
  markInstallReferrerRead();
  return parseReferrerToken(referrer);
}

export default InstallReferrer;
